import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Card, Form, Button, Alert, Row, Col, Spinner, Badge,
  Modal, OverlayTrigger, Tooltip, Collapse, ProgressBar
} from 'react-bootstrap';
import {
  FaSearch, FaBrain, FaTags, FaChevronDown, FaChevronUp,
  FaCopy, FaExternalLinkAlt, FaFilter, FaSlidersH,
  FaLightbulb, FaClock, FaShieldAlt, FaStar, FaChartLine,
  FaProjectDiagram, FaEye, FaTimes, FaRedo, FaMagic,
  FaCheckCircle, FaExclamationTriangle, FaInfoCircle,
  FaDatabase, FaLayerGroup, FaFingerprint, FaHistory
} from 'react-icons/fa';
import knowledgeBaseApi from '../../services/knowledgeBaseApi';

// ──────────────────────────────────────────────────────────
// Helper: truncate text
// ──────────────────────────────────────────────────────────
const truncate = (text, max = 280) => {
  if (!text) return '';
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '…';
};

// ──────────────────────────────────────────────────────────
// Helper: format date
// ──────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

// ──────────────────────────────────────────────────────────
// Helper: score colour
// ──────────────────────────────────────────────────────────
const scoreColor = (val, max = 1) => {
  const pct = (val / max) * 100;
  if (pct >= 75) return '#22c55e';
  if (pct >= 50) return '#eab308';
  if (pct >= 25) return '#f97316';
  return '#ef4444';
};

const scoreBg = (val, max = 1) => {
  const pct = (val / max) * 100;
  if (pct >= 75) return 'rgba(34,197,94,0.12)';
  if (pct >= 50) return 'rgba(234,179,8,0.12)';
  if (pct >= 25) return 'rgba(249,115,22,0.12)';
  return 'rgba(239,68,68,0.12)';
};

// ──────────────────────────────────────────────────────────
// Score Pill Component
// ──────────────────────────────────────────────────────────
const ScorePill = ({ label, value, icon: Icon, max = 1, tip }) => {
  const pct = Math.round((value / max) * 100);
  const color = scoreColor(value, max);
  const bg = scoreBg(value, max);

  const pill = (
    <span className="kq-score-pill" style={{ background: bg, borderColor: color + '40' }}>
      {Icon && <Icon style={{ color, fontSize: '0.65rem' }} />}
      <span className="kq-score-label">{label}</span>
      <span className="kq-score-value" style={{ color }}>{pct}%</span>
    </span>
  );

  if (tip) {
    return (
      <OverlayTrigger placement="top" overlay={<Tooltip>{tip}</Tooltip>}>
        {pill}
      </OverlayTrigger>
    );
  }
  return pill;
};

// ──────────────────────────────────────────────────────────
// Mini bar chart for score breakdown
// ──────────────────────────────────────────────────────────
const ScoreBreakdown = ({ result, weights }) => {
  const bars = [
    { label: 'Vector', value: result.vector_score || 0, weight: weights?.vector || 0.5, color: '#3b82f6' },
    { label: 'Text', value: result.text_score || 0, weight: weights?.text || 0.15, color: '#8b5cf6' },
    { label: 'Credibility', value: result.credibility_score || 0, weight: weights?.credibility || 0.15, color: '#22c55e' },
    { label: 'Trust', value: result.trust_score || 0, weight: weights?.trust || 0.1, color: '#eab308' },
    { label: 'Quality', value: result.quality_score || 0, weight: weights?.quality || 0.1, color: '#f97316' },
  ];

  return (
    <div className="kq-score-breakdown">
      {bars.map((bar) => (
        <div key={bar.label} className="kq-breakdown-row">
          <span className="kq-breakdown-label">{bar.label} <span className="kq-breakdown-weight">×{bar.weight}</span></span>
          <div className="kq-breakdown-bar-track">
            <div
              className="kq-breakdown-bar-fill"
              style={{
                width: `${Math.min(bar.value * 100, 100)}%`,
                background: bar.color,
              }}
            />
          </div>
          <span className="kq-breakdown-val" style={{ color: bar.color }}>
            {(bar.value || 0).toFixed(3)}
          </span>
        </div>
      ))}
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// Quality Metrics Mini Panel
// ──────────────────────────────────────────────────────────
const QualityPanel = ({ metrics }) => {
  if (!metrics) return <span className="text-muted small">No quality data</span>;

  return (
    <div className="kq-quality-panel">
      <div className="kq-quality-grid">
        <div className="kq-quality-item">
          <span className="kq-quality-num">{metrics.retrieval_count || 0}</span>
          <span className="kq-quality-label">Retrievals</span>
        </div>
        <div className="kq-quality-item">
          <span className="kq-quality-num">{metrics.usage_count || 0}</span>
          <span className="kq-quality-label">Uses</span>
        </div>
        <div className="kq-quality-item">
          <span className="kq-quality-num">{metrics.success_count || 0}</span>
          <span className="kq-quality-label">Successes</span>
        </div>
        <div className="kq-quality-item">
          <span className="kq-quality-num" style={{ color: metrics.failure_count > 0 ? '#ef4444' : undefined }}>
            {metrics.failure_count || 0}
          </span>
          <span className="kq-quality-label">Failures</span>
        </div>
      </div>
      <div className="kq-quality-scores">
        <ScorePill label="Helpful" value={metrics.helpfulness_score || 0} icon={FaStar} tip="Helpfulness score" />
        <ScorePill label="Accuracy" value={metrics.accuracy_score || 0} icon={FaCheckCircle} tip="Accuracy score" />
        <ScorePill label="Relevance" value={metrics.relevance_score || 0} icon={FaSearch} tip="Relevance score" />
        <ScorePill label="Recency" value={metrics.recency_score || 0} icon={FaClock} tip="Recency score" />
      </div>
      {metrics.score_trend && (
        <div className="kq-quality-trend">
          <FaChartLine className="me-1" />
          Trend: <strong>{metrics.score_trend}</strong>
          {metrics.last_used && <span className="ms-2 text-muted">· Last used {formatDate(metrics.last_used)}</span>}
        </div>
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────
// Single Result Card
// ──────────────────────────────────────────────────────────
const ResultCard = ({ result, index, weights, onViewDetail }) => {
  const [expanded, setExpanded] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [copied, setCopied] = useState(false);

  const combinedPct = Math.round((result.combined_score || 0) * 100);
  const combinedColor = scoreColor(result.combined_score || 0);

  const handleCopy = () => {
    navigator.clipboard.writeText(result.knowledge_text || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="kq-result-card" style={{ '--rank-color': combinedColor }}>
      {/* Rank indicator */}
      <div className="kq-result-rank" style={{ background: combinedColor + '20', borderColor: combinedColor }}>
        <span className="kq-rank-number">#{index + 1}</span>
        <span className="kq-rank-score" style={{ color: combinedColor }}>{combinedPct}%</span>
      </div>

      {/* Main content area */}
      <div className="kq-result-content">
        {/* Header row */}
        <div className="kq-result-header">
          <div className="kq-result-meta-row">
            {result.learning_type && (
              <Badge className="kq-type-badge">{result.learning_type}</Badge>
            )}
            {result.learning_classification && (
              <Badge className="kq-classification-badge">{result.learning_classification}</Badge>
            )}
            {result.scope && (
              <Badge className="kq-scope-badge">{result.scope}</Badge>
            )}
            {result.primary_category && (
              <Badge className="kq-category-badge">{result.primary_category}</Badge>
            )}
            {result.requires_review && (
              <OverlayTrigger placement="top" overlay={<Tooltip>Requires review</Tooltip>}>
                <Badge bg="warning" text="dark" className="kq-review-badge">
                  <FaExclamationTriangle className="me-1" />Review
                </Badge>
              </OverlayTrigger>
            )}
          </div>

          <div className="kq-result-actions">
            <OverlayTrigger placement="top" overlay={<Tooltip>{copied ? 'Copied!' : 'Copy text'}</Tooltip>}>
              <button className="kq-action-btn" onClick={handleCopy}>
                {copied ? <FaCheckCircle className="text-success" /> : <FaCopy />}
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Score breakdown</Tooltip>}>
              <button className={`kq-action-btn ${showScores ? 'active' : ''}`} onClick={() => setShowScores(!showScores)}>
                <FaSlidersH />
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>View detail</Tooltip>}>
              <button className="kq-action-btn" onClick={() => onViewDetail(result)}>
                <FaEye />
              </button>
            </OverlayTrigger>
          </div>
        </div>

        {/* Summary / Topic hint */}
        {(result.summary || result.topic_hint) && (
          <div className="kq-result-summary">
            <FaLightbulb className="kq-summary-icon" />
            {result.summary || result.topic_hint}
          </div>
        )}

        {/* Knowledge text preview */}
        <div className="kq-result-text" onClick={() => setExpanded(!expanded)}>
          {expanded ? result.knowledge_text : truncate(result.knowledge_text, 320)}
          {result.knowledge_text && result.knowledge_text.length > 320 && (
            <button className="kq-expand-btn">
              {expanded ? <><FaChevronUp /> Less</> : <><FaChevronDown /> More</>}
            </button>
          )}
        </div>

        {/* Score pills row */}
        <div className="kq-scores-row">
          <ScorePill label="Confidence" value={result.confidence || 0} icon={FaShieldAlt} tip="Confidence score" />
          <ScorePill label="Trust" value={result.trust_score || 0} icon={FaShieldAlt} tip="Trust score" />
          <ScorePill label="Credibility" value={result.credibility_score || 0} icon={FaCheckCircle} tip="Credibility score" />
          {result.importance_score > 0 && (
            <ScorePill label="Importance" value={result.importance_score || 0} icon={FaStar} tip="Importance score" />
          )}
          {result.peer_credibility_score > 0 && (
            <ScorePill label="Peer" value={result.peer_credibility_score || 0} icon={FaProjectDiagram} tip="Peer credibility" />
          )}
        </div>

        {/* Score breakdown (collapsible) */}
        <Collapse in={showScores}>
          <div>
            <ScoreBreakdown result={result} weights={weights} />
          </div>
        </Collapse>

        {/* Footer metadata */}
        <div className="kq-result-footer">
          <div className="kq-footer-left">
            {result.source_type && (
              <span className="kq-footer-item">
                <FaDatabase /> {result.source_type}
              </span>
            )}
            {result.micro_agent_id && (
              <span className="kq-footer-item">
                <FaLayerGroup /> Agent {result.micro_agent_id}
              </span>
            )}
            {result.access_count > 0 && (
              <span className="kq-footer-item">
                <FaEye /> {result.access_count} views
              </span>
            )}
            {result.calibration_count > 0 && (
              <span className="kq-footer-item">
                <FaHistory /> {result.calibration_count} calibrations
              </span>
            )}
          </div>
          <div className="kq-footer-right">
            <span className="kq-footer-date">
              <FaClock /> {formatDate(result.created_at)}
            </span>
            <span className="kq-footer-id">
              <FaFingerprint /> {(result.learning_id || '').slice(0, 8)}
            </span>
          </div>
        </div>

        {/* Quality metrics (if present) */}
        {result.quality_metrics && showScores && (
          <div className="kq-quality-section">
            <div className="kq-quality-header">
              <FaChartLine className="me-1" /> Quality Metrics
            </div>
            <QualityPanel metrics={result.quality_metrics} />
          </div>
        )}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════
const QueryKnowledge = ({ agentInfo, selectedAgent }) => {
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState(null);
  const [detailModal, setDetailModal] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);

  // Advanced filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    max_results: 20,
    min_credibility: '',
    min_trust: '',
    min_importance: '',
    include_quality_metrics: true,
    include_source: true,
    include_calibration: false,
    include_nodes: false,
  });

  const searchInputRef = useRef(null);
  const resultsRef = useRef(null);

  // Focus search on mount
  useEffect(() => {
    if (searchInputRef.current) searchInputRef.current.focus();
  }, []);

  // ── Execute search ──
  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q || q.length < 3) {
      setError('Please enter at least 3 characters.');
      return;
    }

    setSearching(true);
    setError(null);
    setSearchResult(null);

    try {
      const options = {
        max_results: parseInt(filters.max_results) || 20,
        include_quality_metrics: filters.include_quality_metrics,
        include_source: filters.include_source,
        include_calibration: filters.include_calibration,
        include_nodes: filters.include_nodes,
      };

      // Agent filter
      if (selectedAgent && selectedAgent !== 'all') {
        options.agent_id = parseInt(selectedAgent);
      }

      // Numeric filters
      if (filters.min_credibility !== '' && !isNaN(parseFloat(filters.min_credibility))) {
        options.min_credibility = parseFloat(filters.min_credibility);
      }
      if (filters.min_trust !== '' && !isNaN(parseFloat(filters.min_trust))) {
        options.min_trust = parseFloat(filters.min_trust);
      }
      if (filters.min_importance !== '' && !isNaN(parseFloat(filters.min_importance))) {
        options.min_importance = parseFloat(filters.min_importance);
      }

      const result = await knowledgeBaseApi.query.smartSearch(q, options);

      setSearchResult(result);

      // Update history
      setSearchHistory(prev => {
        const updated = [{ query: q, count: result.count || 0, time: new Date() }, ...prev.filter(h => h.query !== q)];
        return updated.slice(0, 10);
      });

      // Scroll to results
      setTimeout(() => {
        if (resultsRef.current) {
          resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 200);

    } catch (err) {
      console.error('Search failed:', err);
      setError(err.message || 'Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  }, [query, filters, selectedAgent]);

  // ── Rerun a history item ──
  const rerunSearch = (historyQuery) => {
    setQuery(historyQuery);
    // Small delay so state updates, then search
    setTimeout(() => {
      handleSearch();
    }, 50);
  };

  // ── Clear everything ──
  const handleClear = () => {
    setQuery('');
    setSearchResult(null);
    setError(null);
    if (searchInputRef.current) searchInputRef.current.focus();
  };

  // ── View detail modal ──
  const openDetail = (result) => setDetailModal(result);
  const closeDetail = () => setDetailModal(null);

  return (
    <div className="kq-container">
      {/* ────── Search Card ────── */}
      <Card className="kq-search-card">
        <Card.Body className="kq-search-body">
          {/* Search input */}
          <Form onSubmit={handleSearch}>
            <div className="kq-search-input-wrapper">
              <div className="kq-search-icon-area">
                {searching ? (
                  <Spinner animation="border" size="sm" variant="info" />
                ) : (
                  <FaBrain className="kq-brain-icon" />
                )}
              </div>
              <Form.Control
                ref={searchInputRef}
                type="text"
                placeholder="Ask anything about your knowledge base..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="kq-search-input"
                disabled={searching}
              />
              <div className="kq-search-btn-group">
                {query && (
                  <button type="button" className="kq-clear-btn" onClick={handleClear} title="Clear">
                    <FaTimes />
                  </button>
                )}
                <button
                  type="button"
                  className={`kq-filter-toggle ${showFilters ? 'active' : ''}`}
                  onClick={() => setShowFilters(!showFilters)}
                  title="Filters"
                >
                  <FaFilter />
                </button>
                <Button
                  type="submit"
                  className="kq-search-btn"
                  disabled={searching || !query.trim()}
                >
                  {searching ? 'Searching…' : 'Search'}
                </Button>
              </div>
            </div>
          </Form>

          {/* Error */}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)} className="kq-error-alert">
              {error}
            </Alert>
          )}

          {/* Filters panel */}
          <Collapse in={showFilters}>
            <div className="kq-filters-panel">
              <div className="kq-filters-title">
                <FaSlidersH className="me-2" /> Advanced Filters
              </div>
              <Row className="g-2">
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label className="kq-filter-label">Max Results</Form.Label>
                    <Form.Select
                      size="sm"
                      className="kq-filter-input"
                      value={filters.max_results}
                      onChange={(e) => setFilters(f => ({ ...f, max_results: e.target.value }))}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label className="kq-filter-label">Min Credibility</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="0.1"
                      min="-1"
                      max="1"
                      placeholder="e.g. 0.3"
                      className="kq-filter-input"
                      value={filters.min_credibility}
                      onChange={(e) => setFilters(f => ({ ...f, min_credibility: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label className="kq-filter-label">Min Trust</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      placeholder="e.g. 0.5"
                      className="kq-filter-input"
                      value={filters.min_trust}
                      onChange={(e) => setFilters(f => ({ ...f, min_trust: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
                <Col xs={6} md={3}>
                  <Form.Group>
                    <Form.Label className="kq-filter-label">Min Importance</Form.Label>
                    <Form.Control
                      size="sm"
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      placeholder="e.g. 0.4"
                      className="kq-filter-input"
                      value={filters.min_importance}
                      onChange={(e) => setFilters(f => ({ ...f, min_importance: e.target.value }))}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="g-2 mt-1">
                <Col xs={6} md={3}>
                  <Form.Check
                    type="switch"
                    id="filter-quality"
                    label="Quality Metrics"
                    className="kq-filter-switch"
                    checked={filters.include_quality_metrics}
                    onChange={(e) => setFilters(f => ({ ...f, include_quality_metrics: e.target.checked }))}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Check
                    type="switch"
                    id="filter-source"
                    label="Source Info"
                    className="kq-filter-switch"
                    checked={filters.include_source}
                    onChange={(e) => setFilters(f => ({ ...f, include_source: e.target.checked }))}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Check
                    type="switch"
                    id="filter-calibration"
                    label="Calibration"
                    className="kq-filter-switch"
                    checked={filters.include_calibration}
                    onChange={(e) => setFilters(f => ({ ...f, include_calibration: e.target.checked }))}
                  />
                </Col>
                <Col xs={6} md={3}>
                  <Form.Check
                    type="switch"
                    id="filter-nodes"
                    label="Knowledge Nodes"
                    className="kq-filter-switch"
                    checked={filters.include_nodes}
                    onChange={(e) => setFilters(f => ({ ...f, include_nodes: e.target.checked }))}
                  />
                </Col>
              </Row>
            </div>
          </Collapse>

          {/* Search history */}
          {searchHistory.length > 0 && !searchResult && (
            <div className="kq-history">
              <span className="kq-history-label"><FaHistory className="me-1" /> Recent:</span>
              {searchHistory.slice(0, 5).map((h, i) => (
                <button key={i} className="kq-history-chip" onClick={() => rerunSearch(h.query)}>
                  {truncate(h.query, 40)}
                  <span className="kq-history-count">{h.count}</span>
                </button>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* ────── Results Area ────── */}
      <div ref={resultsRef}>
        {searching && (
          <div className="kq-loading">
            <div className="kq-loading-animation">
              <Spinner animation="border" variant="info" />
            </div>
            <div className="kq-loading-text">
              <FaMagic className="me-2" />
              Expanding query with AI and searching knowledge base…
            </div>
          </div>
        )}

        {searchResult && !searching && (
          <div className="kq-results-container">
            {/* Results header */}
            <div className="kq-results-header">
              <div className="kq-results-header-left">
                <h6 className="kq-results-title">
                  <FaSearch className="me-2" />
                  {searchResult.count || 0} Results
                </h6>
                <span className="kq-results-query">
                  for "<em>{searchResult.query}</em>"
                </span>
              </div>
              <div className="kq-results-header-right">
                <button className="kq-action-btn" onClick={() => handleSearch()} title="Re-run search">
                  <FaRedo />
                </button>
              </div>
            </div>

            {/* Query expansion info */}
            {searchResult.query_expansion && (
              <div className="kq-expansion-panel">
                <div className="kq-expansion-header">
                  <FaBrain className="me-2 text-info" />
                  <strong>AI Query Expansion</strong>
                </div>
                <div className="kq-expansion-body">
                  <div className="kq-expansion-row">
                    <span className="kq-expansion-label">Intent</span>
                    <span className="kq-expansion-value">{searchResult.query_expansion.intent}</span>
                  </div>
                  {searchResult.query_expansion.search_keywords && (
                    <div className="kq-expansion-row">
                      <span className="kq-expansion-label">Keywords</span>
                      <span className="kq-expansion-value kq-expansion-keywords">
                        {searchResult.query_expansion.search_keywords}
                      </span>
                    </div>
                  )}
                  {searchResult.query_expansion.entities && searchResult.query_expansion.entities.length > 0 && (
                    <div className="kq-expansion-row">
                      <span className="kq-expansion-label">Entities</span>
                      <div className="kq-expansion-value">
                        {searchResult.query_expansion.entities.map((e, i) => (
                          <Badge key={i} className="kq-entity-badge">{e}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {searchResult.query_expansion.expanded_text && (
                    <div className="kq-expansion-row">
                      <span className="kq-expansion-label">Expanded</span>
                      <span className="kq-expansion-value kq-expansion-expanded">
                        {searchResult.query_expansion.expanded_text}
                      </span>
                    </div>
                  )}
                </div>

                {/* Scoring weights legend */}
                {searchResult.scoring_weights && (
                  <div className="kq-weights-row">
                    <span className="kq-weights-label">Scoring:</span>
                    {Object.entries(searchResult.scoring_weights).map(([k, v]) => (
                      <span key={k} className="kq-weight-chip">
                        {k} <strong>{Math.round(v * 100)}%</strong>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Results list */}
            {searchResult.results && searchResult.results.length > 0 ? (
              <div className="kq-results-list">
                {searchResult.results.map((result, index) => (
                  <ResultCard
                    key={result.learning_id || index}
                    result={result}
                    index={index}
                    weights={searchResult.scoring_weights}
                    onViewDetail={openDetail}
                  />
                ))}
              </div>
            ) : (
              <div className="kq-no-results">
                <FaInfoCircle className="kq-no-results-icon" />
                <p>No results found. Try broadening your search or adjusting filters.</p>
              </div>
            )}
          </div>
        )}

        {/* Empty state when no search yet */}
        {!searchResult && !searching && (
          <div className="kq-empty-state">
            <FaBrain className="kq-empty-icon" />
            <h6 className="kq-empty-title">Smart Knowledge Search</h6>
            <p className="kq-empty-desc">
              Ask a natural language question. The AI will expand your query,
              generate embeddings, and find the most relevant knowledge using
              hybrid vector + text + credibility scoring.
            </p>
            <div className="kq-empty-examples">
              <span className="kq-example-label">Try:</span>
              {[
                'How does the deployment pipeline work?',
                'What are the error handling patterns we use?',
                'Tell me about the knowledge calibration process',
              ].map((ex, i) => (
                <button
                  key={i}
                  className="kq-example-chip"
                  onClick={() => { setQuery(ex); searchInputRef.current?.focus(); }}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ────── Detail Modal ────── */}
      <Modal
        show={!!detailModal}
        onHide={closeDetail}
        size="xl"
        centered
        contentClassName="kq-detail-modal"
      >
        {detailModal && (
          <>
            <Modal.Header closeButton className="kq-detail-header">
              <Modal.Title className="kq-detail-title">
                <FaDatabase className="me-2" />
                Learning Detail
                <Badge className="kq-detail-id ms-2">
                  {(detailModal.learning_id || '').slice(0, 12)}…
                </Badge>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="kq-detail-body">
              {/* Meta badges */}
              <div className="kq-detail-meta">
                {detailModal.learning_type && <Badge className="kq-type-badge">{detailModal.learning_type}</Badge>}
                {detailModal.learning_classification && <Badge className="kq-classification-badge">{detailModal.learning_classification}</Badge>}
                {detailModal.scope && <Badge className="kq-scope-badge">{detailModal.scope}</Badge>}
                {detailModal.primary_category && <Badge className="kq-category-badge">{detailModal.primary_category}</Badge>}
                {detailModal.status && <Badge className={`kq-status-badge kq-status-${detailModal.status}`}>{detailModal.status}</Badge>}
              </div>

              {/* Summary */}
              {(detailModal.summary || detailModal.topic_hint) && (
                <div className="kq-detail-summary">
                  <FaLightbulb className="me-2 text-warning" />
                  <strong>{detailModal.summary || detailModal.topic_hint}</strong>
                </div>
              )}

              {/* Full knowledge text */}
              <div className="kq-detail-text-container">
                <div className="kq-detail-text-label">Knowledge Text</div>
                <div className="kq-detail-text">
                  {detailModal.knowledge_text}
                </div>
              </div>

              {/* Scores grid */}
              <div className="kq-detail-scores-grid">
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Combined</span>
                  <span className="kq-detail-score-num" style={{ color: scoreColor(detailModal.combined_score || 0) }}>
                    {Math.round((detailModal.combined_score || 0) * 100)}%
                  </span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Vector</span>
                  <span className="kq-detail-score-num">{((detailModal.vector_score || 0) * 100).toFixed(1)}%</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Text</span>
                  <span className="kq-detail-score-num">{(detailModal.text_score || 0).toFixed(4)}</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Confidence</span>
                  <span className="kq-detail-score-num">{Math.round((detailModal.confidence || 0) * 100)}%</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Trust</span>
                  <span className="kq-detail-score-num">{Math.round((detailModal.trust_score || 0) * 100)}%</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Credibility</span>
                  <span className="kq-detail-score-num">{(detailModal.credibility_score || 0).toFixed(4)}</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Importance</span>
                  <span className="kq-detail-score-num">{(detailModal.importance_score || 0).toFixed(4)}</span>
                </div>
                <div className="kq-detail-score-card">
                  <span className="kq-detail-score-title">Peer Cred</span>
                  <span className="kq-detail-score-num">{(detailModal.peer_credibility_score || 0).toFixed(4)}</span>
                </div>
              </div>

              {/* Metadata rows */}
              <div className="kq-detail-metadata">
                <Row className="g-2">
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Agent ID</span>
                      <span className="kq-detail-meta-value">{detailModal.agent_id || '—'}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Micro Agent</span>
                      <span className="kq-detail-meta-value">{detailModal.micro_agent_id || '—'}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Source Type</span>
                      <span className="kq-detail-meta-value">{detailModal.source_type || '—'}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Source Priority</span>
                      <span className="kq-detail-meta-value">{detailModal.source_priority || '—'}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Created</span>
                      <span className="kq-detail-meta-value">{formatDate(detailModal.created_at)}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Updated</span>
                      <span className="kq-detail-meta-value">{formatDate(detailModal.updated_at)}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Access Count</span>
                      <span className="kq-detail-meta-value">{detailModal.access_count || 0}</span>
                    </div>
                  </Col>
                  <Col sm={6} md={3}>
                    <div className="kq-detail-meta-item">
                      <span className="kq-detail-meta-label">Calibrations</span>
                      <span className="kq-detail-meta-value">{detailModal.calibration_count || 0}</span>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Quality metrics in detail view */}
              {detailModal.quality_metrics && (
                <div className="kq-detail-section">
                  <div className="kq-detail-section-title"><FaChartLine className="me-2" /> Quality Metrics</div>
                  <QualityPanel metrics={detailModal.quality_metrics} />
                </div>
              )}

              {/* Source identifier */}
              {detailModal.source_identifier && (
                <div className="kq-detail-section">
                  <div className="kq-detail-section-title"><FaDatabase className="me-2" /> Source Identifier</div>
                  <code className="kq-detail-source-id">{detailModal.source_identifier}</code>
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="kq-detail-footer">
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(detailModal.learning_id || '');
                }}
              >
                <FaCopy className="me-1" /> Copy ID
              </Button>
              <Button
                variant="outline-info"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(detailModal.knowledge_text || '');
                }}
              >
                <FaCopy className="me-1" /> Copy Text
              </Button>
              <Button variant="secondary" size="sm" onClick={closeDetail}>Close</Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </div>
  );
};

export default QueryKnowledge;
